import { DownloadIcon } from '@chakra-ui/icons';
import { Box, HStack, IconButton, Input, Switch, VStack, useDisclosure } from '@chakra-ui/react';
import { useToastWrapper } from 'hooks/useToastWrapper';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import Button from 'components/Button';
import Icon from 'components/Icon';
import { SEO } from 'components/SEO';
import SplitPane from 'components/SplitPane';
import Text from 'components/Text';
import { useMediaQueries } from 'layout/MediaQueriesProvider';
import { ERoute } from 'types/frontend';

import AIReportPanel from './components/AIReportPanel';
import CVEditorSkeleton from './components/CVEditorSkeleton';
import CVForm from './components/CVForm';
import CVPreview from './components/CVPreview';
import CVSettingsPopover from './components/CVSettingsPopover';
import ExportDialog from './components/ExportDialog';
import { useGetCVQuery } from './hooks/useGetCVQuery';
import { useSaveCV } from './hooks/useSaveCV';
import { useToggleCVShare } from './hooks/useToggleCVShare';
import { CVData, CVFontFamily, CVFontSize, CVPersonalInfo, CVSettings, CVTemplate } from './types';
import { createDefaultCVData, createDefaultSettings } from './utils/cvDefaults';
import { buildCVShareUrl } from './utils/cvShare';

const CVEditor = () => {
  const { cvId } = useParams<{ cvId: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobile } = useMediaQueries();
  const { cv, isFetching } = useGetCVQuery(cvId);
  const { saveImmediately, isSaving } = useSaveCV(cvId);
  const { toggleShare, isToggling } = useToggleCVShare(cvId);
  const { toastSuccess, toastInfo } = useToastWrapper();
  const isShared = cv?.is_shared ?? false;

  const [title, setTitle] = useState('');
  const [template, setTemplate] = useState<CVTemplate>('classic');
  const [data, setData] = useState<CVData>(createDefaultCVData());
  const [settings, setSettings] = useState<CVSettings>(createDefaultSettings());
  const [isDirty, setIsDirty] = useState(false);
  const [showEditor, setShowEditor] = useState(!isMobile);
  const [showFormPanel, setShowFormPanel] = useState(() => {
    return searchParams.get('preview') !== 'true';
  });
  const exportDialog = useDisclosure();
  const aiReportPanel = useDisclosure();
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (cv && !hasInitialized.current) {
      hasInitialized.current = true;
      setTitle(cv.title);
      setTemplate(cv.template);
      const loadedData = cv.data || createDefaultCVData();
      setData({
        ...loadedData,
        personalInfo: {
          ...createDefaultCVData().personalInfo,
          ...loadedData.personalInfo,
        },
      });
      setSettings({
        ...createDefaultSettings(),
        ...(cv.settings || {}),
      });
    }
  }, [cv]);

  // Auto-open AI review panel if ?ai-review=true
  useEffect(() => {
    if (searchParams.get('ai-review') === 'true' && cv) {
      aiReportPanel.onOpen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cv]);

  // Show info toast if AI generation was truncated (max_tokens hit)
  useEffect(() => {
    const state = location.state as { truncated?: boolean } | null;
    if (state?.truncated) {
      toastInfo('We extracted as much data as possible. Some sections may need manual edits.');
      // Clear the state so it doesn't show again on re-render
      navigate(location.pathname, { replace: true, state: undefined });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-save every 10 seconds when dirty
  useEffect(() => {
    if (!isDirty || !hasInitialized.current) return;

    const timer = setTimeout(() => {
      saveImmediately({ title, template, data, settings });
      setIsDirty(false);
    }, 10_000);

    return () => clearTimeout(timer);
  }, [isDirty, title, template, data, settings, saveImmediately]);

  // Warn on browser tab close/refresh if auto-save hasn't caught up yet
  useEffect(() => {
    if (!isDirty) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const markDirty = useCallback(() => setIsDirty(true), []);

  const handleSave = useCallback(() => {
    saveImmediately({ title, template, data, settings });
    setIsDirty(false);
    toastSuccess('CV saved');
  }, [title, template, data, settings, saveImmediately, toastSuccess]);

  const handleOpenAIReview = useCallback(() => {
    if (isDirty) {
      saveImmediately({ title, template, data, settings });
      setIsDirty(false);
    }

    aiReportPanel.onOpen();
  }, [isDirty, title, template, data, settings, saveImmediately, aiReportPanel]);

  const handleTitleChange = useCallback(
    (newTitle: string) => {
      setTitle(newTitle);
      markDirty();
    },
    [markDirty]
  );

  const handleTemplateChange = useCallback(
    (newTemplate: CVTemplate) => {
      setTemplate(newTemplate);

      // When switching to modern, auto-fix dark accent colors that look bad as sidebar bg
      const darkColors = ['#1A202C', '#2D3748'];
      if (newTemplate === 'modern' && darkColors.includes(settings.accentColor)) {
        setSettings((prev) => ({ ...prev, accentColor: '#6B46C1' }));
      }
      markDirty();
    },
    [settings.accentColor, markDirty]
  );

  const handleDataChange = useCallback(
    (newData: CVData) => {
      setData(newData);
      markDirty();
    },
    [markDirty]
  );

  const handleFontChange = useCallback(
    (fontFamily: CVFontFamily) => {
      setSettings((prev) => ({ ...prev, fontFamily }));
      markDirty();
    },
    [markDirty]
  );

  const handleFontSizeChange = useCallback(
    (fontSize: CVFontSize) => {
      setSettings((prev) => ({ ...prev, fontSize }));
      markDirty();
    },
    [markDirty]
  );

  const handleColorChange = useCallback(
    (accentColor: string) => {
      setSettings((prev) => ({ ...prev, accentColor }));
      markDirty();
    },
    [markDirty]
  );

  const handleOpacityChange = useCallback(
    (accentOpacity: number) => {
      setSettings((prev) => ({ ...prev, accentOpacity }));
      markDirty();
    },
    [markDirty]
  );

  const handlePersonalInfoChange = useCallback(
    (field: keyof CVPersonalInfo, value: string | boolean) => {
      setData((prev) => ({
        ...prev,
        personalInfo: { ...prev.personalInfo, [field]: value },
      }));
      markDirty();
    },
    [markDirty]
  );

  const handleSidebarReversedToggle = useCallback(() => {
    setSettings((prev) => ({ ...prev, sidebarReversed: !prev.sidebarReversed }));
    markDirty();
  }, [markDirty]);

  const handleShowPhotoToggle = useCallback(() => {
    setSettings((prev) => ({ ...prev, showPhoto: !prev.showPhoto }));
    markDirty();
  }, [markDirty]);

  const handleHeaderBgToggle = useCallback(() => {
    setSettings((prev) => ({ ...prev, headerBackground: !prev.headerBackground }));
    markDirty();
  }, [markDirty]);


  const handleCopyShareLink = useCallback(async () => {
    if (!cvId) return;

    try {
      await navigator.clipboard.writeText(buildCVShareUrl(cvId));
      toastSuccess('Share link copied to clipboard');
    } catch {
      // Clipboard API may fail in some environments
    }
  }, [cvId, toastSuccess]);

  const handleShareToggle = useCallback(
    (shared: boolean) => {
      toggleShare(shared);
    },
    [toggleShare]
  );

  const handleBack = useCallback(() => {
    if (isDirty) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to leave?');
      if (!confirmed) return;
    }

    navigate(ERoute.CVBuilder);
  }, [isDirty, navigate]);

  if ((isFetching && !cv) || !hasInitialized.current) {
    return <CVEditorSkeleton />;
  }

  if (!cv && !isFetching) {
    return (
      <VStack mt="20vh" spacing={4}>
        <Text fontSize="xLarge">CV not found</Text>
        <Text fontSize="small" opacity="secondary">
          This CV may have been deleted or doesn&apos;t exist.
        </Text>
      </VStack>
    );
  }

  const shareUrl = cvId ? buildCVShareUrl(cvId) : '';

  const renderToolbar = () => {
    if (isMobile) {
      return (
        <HStack
          height="48px"
          minHeight="48px"
          borderBottom="1px solid"
          borderColor="whiteAlpha.200"
          backgroundColor="gray.800"
          flexShrink={0}
          width="100%"
          position="sticky"
          top="-16px"
          zIndex={5}
          px={2}
          spacing={1}
          justifyContent="space-between"
        >
          {/* Left: Back + Save */}
          <HStack spacing={1} flexShrink={0}>
            <IconButton
              aria-label="Back"
              icon={<Icon type="BackArrow" size={14} />}
              variant="ghost"
              size="sm"
              h="34px"
              w="34px"
              minW="34px"
              onClick={handleBack}
            />
            <IconButton
              aria-label="Save"
              icon={
                <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
              }
              variant="ghost"
              size="sm"
              h="34px"
              w="34px"
              minW="34px"
              onClick={handleSave}
              isLoading={isSaving}
            />
          </HStack>

          {/* Center: Title + Settings */}
          <HStack spacing={1} flex={1} minWidth={0} justifyContent="center">
            <Input
              value={title}
              onChange={(e) => handleTitleChange(e.target.value)}
              variant="unstyled"
              fontWeight="500"
              fontSize="sm"
              placeholder="Untitled CV"
              textAlign="center"
              _placeholder={{ color: 'whiteAlpha.300' }}
              minWidth="60px"
            />
            <Box flexShrink={0}>
              <CVSettingsPopover
                settings={settings}
                template={template}
                personalInfo={data.personalInfo}
                onTemplateChange={handleTemplateChange}
                onFontChange={handleFontChange}
                onFontSizeChange={handleFontSizeChange}
                onColorChange={handleColorChange}
                onOpacityChange={handleOpacityChange}
                onSidebarReversedToggle={handleSidebarReversedToggle}
                onShowPhotoToggle={handleShowPhotoToggle}
                onHeaderBgToggle={handleHeaderBgToggle}
                onPersonalInfoChange={handlePersonalInfoChange}
                isShared={isShared}
                shareUrl={shareUrl}
                onShareToggle={handleShareToggle}
                onCopyShareLink={handleCopyShareLink}
                isShareToggling={isToggling}
              />
            </Box>
          </HStack>

          {/* Right: Toggle + Export + AI Review (icon-only) */}
          <HStack spacing={1} flexShrink={0}>
            <IconButton
              aria-label={showEditor ? 'Preview' : 'Editor'}
              icon={<Icon type={showEditor ? 'Preview' : 'Code'} size={16} />}
              variant="ghost"
              size="sm"
              h="34px"
              w="34px"
              minW="34px"
              onClick={() => setShowEditor(!showEditor)}
            />
            <IconButton
              aria-label="Export PDF"
              icon={<DownloadIcon boxSize={4} />}
              variant="ghost"
              size="sm"
              h="34px"
              w="34px"
              minW="34px"
              onClick={exportDialog.onOpen}
              color="purple.300"
            />
            <IconButton
              aria-label="AI Review"
              icon={<Icon type="GenerateCodeSolution" size={16} />}
              variant="ghost"
              size="sm"
              h="34px"
              w="34px"
              minW="34px"
              onClick={handleOpenAIReview}
            />
          </HStack>
        </HStack>
      );
    }

    return (
      <Box
        height="48px"
        minHeight="48px"
        borderBottom="1px solid"
        borderColor="whiteAlpha.200"
        backgroundColor="gray.800"
        flexShrink={0}
        width="100%"
        position="sticky"
        top="-16px"
        zIndex={5}
      >
        {/* Center: Title + Settings — absolutely positioned for true centering */}
        <HStack
          position="absolute"
          left="50%"
          top="50%"
          transform="translate(-50%, -50%)"
          spacing={0}
          h="100%"
          zIndex={1}
        >
          <Box position="relative" display="inline-flex" alignItems="center">
            {/* Hidden measurer — drives the width */}
            <Box
              as="span"
              visibility="hidden"
              whiteSpace="pre"
              fontWeight="500"
              fontSize="sm"
              px="1px"
              minWidth="60px"
              maxWidth="200px"
            >
              {title || 'Untitled CV'}
            </Box>
            <Input
              value={title}
              onChange={(e) => handleTitleChange(e.target.value)}
              variant="unstyled"
              fontWeight="500"
              fontSize="sm"
              placeholder="Untitled CV"
              textAlign="center"
              position="absolute"
              left={0}
              width="100%"
              _placeholder={{ color: 'whiteAlpha.300' }}
            />
          </Box>
          <Box display="flex" alignItems="center" flexShrink={0} ml={1}>
            <CVSettingsPopover
              settings={settings}
              template={template}
              personalInfo={data.personalInfo}
              onTemplateChange={handleTemplateChange}
              onFontChange={handleFontChange}
              onFontSizeChange={handleFontSizeChange}
              onColorChange={handleColorChange}
              onOpacityChange={handleOpacityChange}
              onSidebarReversedToggle={handleSidebarReversedToggle}
              onShowPhotoToggle={handleShowPhotoToggle}
              onHeaderBgToggle={handleHeaderBgToggle}
              onPersonalInfoChange={handlePersonalInfoChange}
              isShared={isShared}
              shareUrl={shareUrl}
              onShareToggle={handleShareToggle}
              onCopyShareLink={handleCopyShareLink}
              isShareToggling={isToggling}
            />
          </Box>
        </HStack>

        <HStack h="100%" spacing={0} justifyContent="space-between" position="relative">
          {/* Left: Back + Save */}
          <HStack spacing={0} h="100%" flexShrink={0}>
            <HStack
              as="button"
              h="100%"
              px={3}
              spacing={1.5}
              cursor="pointer"
              onClick={handleBack}
              _hover={{ backgroundColor: 'whiteAlpha.50' }}
              transition="background-color 0.15s"
              borderRight="1px solid"
              borderColor="whiteAlpha.100"
              flexShrink={0}
            >
              <Icon type="BackArrow" size={12} />
              <Text fontSize="micro" opacity="secondary">
                Back
              </Text>
            </HStack>
            <Box display="flex" alignItems="center" h="100%" px={2}>
              <Button
                scheme="gradient"
                size="sm"
                onClick={handleSave}
                isLoading={isSaving}
                h="30px"
                px={3}
              >
                Save CV
              </Button>
            </Box>
          </HStack>

          {/* Right: Toggle editor + Export + AI Review */}
          <HStack spacing={2} h="100%" flexShrink={0} pr={3}>
            <HStack spacing={1.5} h="100%" px={2}>
              <Text fontSize="micro" opacity="secondary">
                Editor
              </Text>
              <Switch
                isChecked={showFormPanel}
                onChange={() => setShowFormPanel(!showFormPanel)}
                colorScheme="purple"
                size="sm"
              />
            </HStack>
            <Button
              scheme="primary"
              size="sm"
              onClick={exportDialog.onOpen}
              leftIcon={<DownloadIcon boxSize={3.5} />}
              h="34px"
              px={3}
            >
              Export PDF
            </Button>
            <Button
              scheme="outline"
              size="sm"
              onClick={handleOpenAIReview}
              leftIcon={<Icon type="GenerateCodeSolution" size={14} />}
              h="34px"
              px={3}
            >
              AI Review
            </Button>
          </HStack>
        </HStack>
      </Box>
    );
  };

  const renderEditor = () => {
    if (isMobile) {
      if (showEditor) {
        return (
          <Box height="calc(100vh - 108px)" overflow="hidden">
            <CVForm data={data} onChange={handleDataChange} cvId={cvId} showPhoto={settings.showPhoto} />
          </Box>
        );
      }

      return <CVPreview data={data} settings={settings} template={template} />;
    }

    if (!showFormPanel) {
      return (
        <Box display="flex" justifyContent="center" width="100%">
          <Box width="55%" maxWidth="800px" minWidth="320px">
            <CVPreview data={data} settings={settings} template={template} />
          </Box>
        </Box>
      );
    }

    return (
      <Box height="calc(100vh - 108px)" overflow="hidden">
        <SplitPane leftPaneSize={55} rightPaneSize={45}>
          <Box height="100%" overflow="auto" display="flex" justifyContent="center" px="5%">
            <Box width="100%">
              <CVPreview data={data} settings={settings} template={template} />
            </Box>
          </Box>
          <CVForm data={data} onChange={handleDataChange} cvId={cvId} showPhoto={settings.showPhoto} />
        </SplitPane>
      </Box>
    );
  };

  return (
    <Box
      minHeight="calc(100vh - 60px)"
      mx={-4}
      mt={-4}
      mb="-60px"
      width="calc(100% + 32px)"
      maxWidth="calc(100% + 32px)"
    >
      <SEO
        title={`BigDevSoon | ${title || 'CV Editor'}`}
        description="Edit your professional CV with real-time preview."
        url={`${process.env.REACT_APP_SITE_URL}cv-builder/${cvId}`}
      />
      {renderToolbar()}
      {renderEditor()}

      <ExportDialog
        isOpen={exportDialog.isOpen}
        onClose={exportDialog.onClose}
        data={data}
        settings={settings}
        template={template}
      />

      {cvId && (
        <AIReportPanel
          isOpen={aiReportPanel.isOpen}
          onClose={aiReportPanel.onClose}
          cvId={cvId}
          report={cv?.ai_report || null}
        />
      )}
    </Box>
  );
};

export default CVEditor;
